import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaHeartbeat, FaBrain, FaClipboardList, FaMoon, FaSun, FaLeaf,
  FaArrowRight, FaCheckCircle
} from 'react-icons/fa';
import Header from './Header';
import Footer from './Footer';
import './Trial.css';

const Trial = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  const phases = [
    {
      number: '01',
      duration: '1 Month',
      label: 'Baseline',
      title: 'Establishing Your Baseline',
      description:
        'Before the real trial begins, we spend one month learning what "normal" looks like for you. No interventions, just quiet, consistent data collection so we have a clean reference point.',
      accent: false,
      details: [
        'Passive wearable data only',
        'Daily check-in logging begins',
        'Calibration of cognitive assessments',
        'No active prompts or experiments',
      ],
    },
    {
      number: '02',
      duration: '3 Months',
      label: 'Active Trial',
      title: 'The Real Experiment',
      description:
        'Three months of structured data collection with the full protocol in place: passive sensing, morning cognitive probes, and bookend check-ins each day. This is where the science happens.',
      accent: true,
      details: [
        'Full passive + active data capture',
        'Daily cognitive reaction-time probes',
        'Morning and evening check-ins',
        'Migraine event logging when relevant',
      ],
    },
  ];

  const pillars = [
    {
      icon: FaHeartbeat,
      title: 'Passive Sensing',
      description:
        'Your smartwatch runs in the background, capturing heart rate variability, sleep architecture, activity levels, and other physiological signals throughout the day and night. No action required from you.',
    },
    {
      icon: FaBrain,
      title: 'Cognitive Probes',
      description:
        'Each morning you complete a brief reaction-time test. It takes roughly 2 minutes and measures subtle shifts in processing speed, a signal we believe may precede migraine onset.',
    },
    {
      icon: FaClipboardList,
      title: 'Active Check-ins',
      description:
        'A short morning and evening questionnaire covers how you slept, current symptom state, stress level, and any migraine events. Straightforward, structured, and quick.',
    },
  ];

  const dailySchedule = [
    {
      time: 'Morning',
      icon: FaSun,
      items: [
        { label: 'Cognitive probe', detail: 'Reaction-time assessment (~2 min)' },
        { label: 'Morning check-in', detail: 'Sleep quality, symptom baseline (~2 min)' },
      ],
    },
    {
      time: 'Throughout the Day',
      icon: FaHeartbeat,
      items: [
        { label: 'Wear the smartwatch', detail: 'Passive physiological sensing, no action needed' },
      ],
    },
    {
      time: 'Evening',
      icon: FaMoon,
      items: [
        { label: 'Evening check-in', detail: 'Symptom review, energy, stress (~2 min)' },
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  return (
    <div className="App">
      <Header isScrolled={isScrolled} />
      <main className="trial-main">

        {/* ── Hero ── */}
        <section className="trial-hero section">
          <div className="container">
            <motion.div
              className="trial-hero-inner"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.span
                className="trial-eyebrow"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                Phase 2 · Where We're Headed
              </motion.span>
              <motion.h1
                className="trial-hero-title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                What the trial<br />
                <span className="highlight">actually involves</span>
              </motion.h1>
              <motion.p
                className="trial-hero-subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                Passive sensing. Active check-ins. Lightweight cognitive probes.
                Four months, 6–8 minutes a day. This is what we're building towards:
                the full trial that follows once our current validation study has confirmed
                what to measure and how.
              </motion.p>
              <motion.div
                className="trial-hero-stats"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                <div className="trial-stat">
                  <div className="trial-stat-number">4 mo</div>
                  <div className="trial-stat-label">Total duration</div>
                </div>
                <div className="trial-stat-divider" />
                <div className="trial-stat">
                  <div className="trial-stat-number">6–8 min</div>
                  <div className="trial-stat-label">Per day</div>
                </div>
                <div className="trial-stat-divider" />
                <div className="trial-stat">
                  <div className="trial-stat-number">3</div>
                  <div className="trial-stat-label">Data streams</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── Three Pillars ── */}
        <section className="trial-pillars section bg-light">
          <div className="container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Three data streams</h2>
              <p className="section-subtitle">
                Each captures a different dimension of your health. Together they
                give us a much richer picture than any single measurement could.
              </p>
            </motion.div>

            <motion.div
              className="pillars-grid"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {pillars.map((p, i) => (
                <motion.div
                  key={i}
                  className="pillar-card"
                  variants={itemVariants}
                  whileHover={{ y: -3, transition: { duration: 0.2 } }}
                >
                  <div className="pillar-icon">
                    <p.icon />
                  </div>
                  <h3 className="pillar-title">{p.title}</h3>
                  <p className="pillar-description">{p.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Daily Schedule ── */}
        <section className="trial-daily section">
          <div className="container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">A day in the trial</h2>
              <p className="section-subtitle">
                Designed to fit around your life, not the other way around.
              </p>
            </motion.div>

            <motion.div
              className="daily-schedule"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {dailySchedule.map((block, i) => (
                <motion.div key={i} className="schedule-block" variants={itemVariants}>
                  <div className="schedule-time-col">
                    <div className="schedule-icon">
                      <block.icon />
                    </div>
                    <div className="schedule-time">{block.time}</div>
                  </div>
                  <div className="schedule-items">
                    {block.items.map((item, j) => (
                      <div key={j} className="schedule-item">
                        <FaCheckCircle className="check-icon" />
                        <div>
                          <div className="schedule-item-label">{item.label}</div>
                          <div className="schedule-item-detail">{item.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}

              <motion.div className="schedule-total" variants={itemVariants}>
                <div className="schedule-total-label">Total daily burden</div>
                <div className="schedule-total-value">6–8 minutes</div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── Timeline ── */}
        <section className="trial-timeline section bg-light">
          <div className="container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Four months, two phases</h2>
              <p className="section-subtitle">
                We start slow on purpose. A clean baseline makes the data from the
                active trial far more meaningful.
              </p>
            </motion.div>

            <motion.div
              className="timeline-grid"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {phases.map((phase, i) => (
                <motion.div
                  key={i}
                  className={`timeline-card ${phase.accent ? 'timeline-card--accent' : ''}`}
                  variants={itemVariants}
                  whileHover={{ y: -3, transition: { duration: 0.2 } }}
                >
                  <div className="timeline-meta">
                    <span className="timeline-number">{phase.number}</span>
                    <span className="timeline-duration">{phase.duration}</span>
                    <span className="timeline-badge">{phase.label}</span>
                  </div>
                  <h3 className="timeline-title">{phase.title}</h3>
                  <p className="timeline-description">{phase.description}</p>
                  <ul className="timeline-details">
                    {phase.details.map((d, j) => (
                      <li key={j} className="timeline-detail-item">
                        <FaArrowRight className="detail-arrow" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── End of Trial ── */}
        <section className="trial-outcome section">
          <div className="container">
            <motion.div
              className="outcome-inner"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <div className="outcome-icon">
                <FaLeaf />
              </div>
              <h2 className="outcome-title">What you get at the end</h2>
              <p className="outcome-description">
                After four months, every participant receives a personal data summary:
                a clear breakdown of the patterns we observed in your physiological and
                cognitive data over the course of the trial.
              </p>
              <div className="outcome-items">
                <div className="outcome-item">
                  <FaCheckCircle className="outcome-check" />
                  <span>Your sleep and HRV trends over time</span>
                </div>
                <div className="outcome-item">
                  <FaCheckCircle className="outcome-check" />
                  <span>Cognitive performance patterns</span>
                </div>
                <div className="outcome-item">
                  <FaCheckCircle className="outcome-check" />
                  <span>Migraine event correlations where detectable</span>
                </div>
                <div className="outcome-item">
                  <FaCheckCircle className="outcome-check" />
                  <span>Insight into your own baseline physiology</span>
                </div>
              </div>
              <motion.button
                className="btn btn-primary btn-large"
                onClick={scrollToContact}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get Involved
                <FaArrowRight className="btn-icon" />
              </motion.button>

              <p className="cds-note">
                The long-term goal is clinical decision support. If reliable precursors
                to migraine onset can be identified from passively collected data, that
                evidence base could eventually inform tools that help clinicians anticipate
                and intervene before an episode occurs.
              </p>
            </motion.div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default Trial;
